import { handleTrainingApiRequest } from './trainingApiMock.js';

const TRAINING_MODE_KEY = 'trainingMode';
const TRAINING_SESSION_BACKUP_KEY = 'trainingModeSessionBackup';
const TRAINING_FETCH_INSTALLED_KEY = '__wkndTrainingFetchInstalled';
const TRAINING_ORIGINAL_FETCH_KEY = '__wkndOriginalFetch';
const MANAGER_TOKEN_KEY = 'managerToken';
const MANAGER_ID_KEY = 'managerID';
const MANAGEMENT_DATA_KEY = 'managementData';
const MANAGER_NAME_KEY = 'managerName';
const MANAGER_AUTHORIZATION_KEY = 'managerAuthorization';

export const TRAINING_MANAGER_ID = 'training-manager';
export const TRAINING_MANAGER_TOKEN = 'training-token';
export const TRAINING_MANAGER_NAME = 'Training Admin';
export const TRAINING_MANAGER_AUTHORIZATION = '1';

export const TRAINING_MANAGEMENT_DATA = [
    {
        countryID: '1',
        name: 'Taiwan',
        cities: [
            {
                cityID: '2',
                name: 'Taipei',
                districts: 'Training Campus',
                language: 'zh'
            },
            {
                cityID: '3',
                name: 'Taichung',
                districts: 'Practice Area',
                language: 'zh'
            }
        ]
    },
    {
        countryID: '2',
        name: 'Japan',
        cities: [
            {
                cityID: '8',
                name: 'Tokyo',
                districts: 'Training Lab',
                language: 'ja'
            }
        ]
    }
];

const canUseLocalStorage = () => typeof localStorage !== 'undefined';

export const isTrainingMode = () => canUseLocalStorage() && localStorage.getItem(TRAINING_MODE_KEY) === 'true';

const getJsonItem = (key) => {
    if (!canUseLocalStorage()) return null;
    try {
        const value = localStorage.getItem(key);
        return value ? JSON.parse(value) : null;
    } catch (error) {
        console.warn(`Unable to parse localStorage item "${key}" for Training Mode:`, error);
        return null;
    }
};

const setJsonItem = (key, value) => {
    if (!canUseLocalStorage()) return;
    localStorage.setItem(key, JSON.stringify(value));
};

const readCurrentSession = () => ({
    managerID: localStorage.getItem(MANAGER_ID_KEY),
    managerToken: localStorage.getItem(MANAGER_TOKEN_KEY),
    managerName: localStorage.getItem(MANAGER_NAME_KEY),
    managerAuthorization: localStorage.getItem(MANAGER_AUTHORIZATION_KEY),
    managementData: getJsonItem(MANAGEMENT_DATA_KEY)
});

const writeSession = (session) => {
    if (session.managerID) localStorage.setItem(MANAGER_ID_KEY, session.managerID);
    if (session.managerToken) localStorage.setItem(MANAGER_TOKEN_KEY, session.managerToken);
    if (session.managerName) localStorage.setItem(MANAGER_NAME_KEY, session.managerName);
    if (session.managerAuthorization) localStorage.setItem(MANAGER_AUTHORIZATION_KEY, session.managerAuthorization);
    if (session.managementData) setJsonItem(MANAGEMENT_DATA_KEY, session.managementData);
};

export const seedTrainingSession = () => {
    if (!canUseLocalStorage()) return;
    localStorage.setItem(MANAGER_ID_KEY, TRAINING_MANAGER_ID);
    localStorage.setItem(MANAGER_TOKEN_KEY, TRAINING_MANAGER_TOKEN);
    localStorage.setItem(MANAGER_NAME_KEY, TRAINING_MANAGER_NAME);
    localStorage.setItem(MANAGER_AUTHORIZATION_KEY, TRAINING_MANAGER_AUTHORIZATION);
    setJsonItem(MANAGEMENT_DATA_KEY, TRAINING_MANAGEMENT_DATA);
};

export const enableTrainingMode = () => {
    if (!canUseLocalStorage()) return;

    if (!isTrainingMode()) {
        localStorage.setItem(TRAINING_SESSION_BACKUP_KEY, JSON.stringify(readCurrentSession()));
    }
    localStorage.setItem(TRAINING_MODE_KEY, 'true');
    seedTrainingSession();
    installTrainingFetchMock();
    applyTrainingModeVisualState();
};

export const disableTrainingMode = () => {
    if (!canUseLocalStorage()) return;

    localStorage.removeItem(TRAINING_MODE_KEY);

    const backupRaw = localStorage.getItem(TRAINING_SESSION_BACKUP_KEY);
    localStorage.removeItem(TRAINING_SESSION_BACKUP_KEY);

    localStorage.removeItem(MANAGER_TOKEN_KEY);
    localStorage.removeItem(MANAGER_ID_KEY);
    localStorage.removeItem(MANAGER_NAME_KEY);
    localStorage.removeItem(MANAGER_AUTHORIZATION_KEY);
    localStorage.removeItem(MANAGEMENT_DATA_KEY);

    if (backupRaw) {
        try {
            const backup = JSON.parse(backupRaw);
            writeSession(backup || {});
        } catch (error) {
            console.warn('Unable to restore previous manager session after Training Mode:', error);
        }
    }

    applyTrainingModeVisualState();
};

export const ensureTrainingSession = () => {
    if (!isTrainingMode()) return;
    const missingTrainingAuth = localStorage.getItem(MANAGER_ID_KEY) !== TRAINING_MANAGER_ID
        || localStorage.getItem(MANAGER_TOKEN_KEY) !== TRAINING_MANAGER_TOKEN;
    if (missingTrainingAuth || !Array.isArray(getJsonItem(MANAGEMENT_DATA_KEY))) {
        seedTrainingSession();
    }
};

export const applyTrainingModeVisualState = () => {
    if (typeof document === 'undefined') return;
    const active = isTrainingMode();
    document.documentElement?.classList?.toggle('training-mode', active);
    document.body?.classList.toggle('training-mode', active);
};

export const isTrainingApiUrl = (url) => {
    if (!url) return false;
    const value = String(url);
    return value.includes('hello-zima.appspot.com/MAPIv1/')
        || value.includes('openrouter.ai/')
        || value.includes('openai.com/')
        || value.includes('generativelanguage.googleapis.com/');
};

export const installTrainingFetchMock = () => {
    if (typeof window === 'undefined' || typeof window.fetch !== 'function') return;
    if (window[TRAINING_FETCH_INSTALLED_KEY]) return;

    window.__wkndTrainingNetworkEvents = window.__wkndTrainingNetworkEvents || [];
    window[TRAINING_ORIGINAL_FETCH_KEY] = window.fetch.bind(window);
    window.fetch = async (input, init = {}) => {
        const url = typeof input === 'string' ? input : input?.url;

        if (isTrainingMode() && isTrainingApiUrl(url)) {
            const mockedResponse = await handleTrainingApiRequest(input, init);
            if (mockedResponse) {
                window.__wkndTrainingNetworkEvents.push({
                    type: 'mock',
                    url: String(url || ''),
                    timestamp: new Date().toISOString()
                });
                return mockedResponse;
            }

            console.warn('[Training Mode] Blocked unmocked API request:', url);
            window.__wkndTrainingNetworkEvents.push({
                type: 'blocked',
                url: String(url || ''),
                timestamp: new Date().toISOString()
            });
            return new Response(JSON.stringify({
                success: 'false',
                error: {
                    errorCode: 'TRAINING_MODE_UNMOCKED_ENDPOINT',
                    errorMessage: 'Training Mode blocked an unmocked API request.'
                }
            }), {
                status: 501,
                headers: { 'Content-Type': 'application/json' }
            });
        }

        return window[TRAINING_ORIGINAL_FETCH_KEY](input, init);
    };

    window[TRAINING_FETCH_INSTALLED_KEY] = true;
};

export const initializeTrainingModeRuntime = () => {
    if (isTrainingMode()) {
        seedTrainingSession();
        installTrainingFetchMock();
    }
    applyTrainingModeVisualState();
};
